import { AfterViewInit, Component, ChangeDetectorRef, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import cloudflareConfig from '../../core/config/cloudflare.config';
import { MetaService } from '../../core/services/meta.service';

declare global {
  interface Window {
    turnstile: any;
  }
}

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, Header, Footer],
  template: `
    <app-header />

    <div class="min-h-screen bg-gray-50 flex items-start justify-center pt-8 pb-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md w-full space-y-8">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Nueva contrasena
          </h2>
          <p class="mt-2 text-center text-sm text-gray-600">
            Ingresa una nueva contrasena para tu cuenta.
          </p>
        </div>

        @if (tokenError) {
          <div class="rounded-md bg-red-50 p-4">
            <p class="text-sm font-medium text-red-800">{{ tokenError }}</p>
          </div>
          <div class="text-center text-sm">
            <a routerLink="/login" class="font-medium text-[#e8343f] hover:text-red-700">
              Volver a iniciar sesion
            </a>
          </div>
        } @else if (!passwordUpdated) {
          <form class="mt-8 space-y-6" [formGroup]="form" (ngSubmit)="onSubmit()">
            <div class="rounded-md shadow-sm space-y-4">
              <div>
                <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-1">
                  Nueva contrasena
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  [type]="showPassword ? 'text' : 'password'"
                  formControlName="newPassword"
                  class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm"
                  [class.border-red-500]="isFieldInvalid('newPassword')"
                  placeholder="Minimo 8 caracteres"
                />
                @if (isFieldInvalid('newPassword')) {
                  <p class="mt-1 text-sm text-red-600">La contrasena debe tener mayusculas, minusculas y numeros</p>
                }
                @if (form.get('newPassword')?.value) {
                  <div class="mt-2">
                    <div class="flex items-center space-x-2">
                      <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          class="h-full transition-all duration-300"
                          [class.w-1/3]="passwordStrength === 'weak'"
                          [class.w-2/3]="passwordStrength === 'medium'"
                          [class.w-full]="passwordStrength === 'strong'"
                          [class.bg-red-500]="passwordStrength === 'weak'"
                          [class.bg-yellow-500]="passwordStrength === 'medium'"
                          [class.bg-green-500]="passwordStrength === 'strong'"
                        ></div>
                      </div>
                      <span class="text-xs font-medium"
                        [class.text-red-500]="passwordStrength === 'weak'"
                        [class.text-yellow-500]="passwordStrength === 'medium'"
                        [class.text-green-500]="passwordStrength === 'strong'"
                      >
                        @if (passwordStrength === 'weak') { Debil }
                        @if (passwordStrength === 'medium') { Media }
                        @if (passwordStrength === 'strong') { Fuerte }
                      </span>
                    </div>
                  </div>
                }
              </div>
              <div>
                <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar contrasena
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  [type]="showConfirmPassword ? 'text' : 'password'"
                  formControlName="confirmPassword"
                  class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm"
                  [class.border-red-500]="passwordMismatch"
                  placeholder="Repite tu contrasena"
                />
                @if (passwordMismatch) {
                  <p class="mt-1 text-sm text-red-600">Las contrasenas no coinciden</p>
                }
              </div>
            </div>

            @if (errorMessage) {
              <div class="rounded-md bg-red-50 p-4">
                <p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
              </div>
            }

            <div class="flex justify-center">
              <div id="turnstile-container-reset"></div>
            </div>

            <button
              type="submit"
              [disabled]="isLoading || form.invalid"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#e8343f] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8343f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              @if (isLoading) {
                Guardando...
              } @else {
                Guardar contrasena
              }
            </button>

            <div class="text-center text-sm">
              <a routerLink="/login" class="font-medium text-[#e8343f] hover:text-red-700">
                Volver a iniciar sesion
              </a>
            </div>
          </form>
        } @else {
          <div class="rounded-md bg-green-50 p-4">
            <p class="text-sm font-medium text-green-800">{{ successMessage }}</p>
          </div>
          <div class="text-center text-sm">
            <a routerLink="/login" class="font-medium text-[#e8343f] hover:text-red-700">
              Ir a iniciar sesion
            </a>
          </div>
        }
      </div>
    </div>

    <app-footer />
  `,
  styles: []
})
export class ResetPasswordComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private metaService = inject(MetaService);
  private isBrowser: boolean;

  form!: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  tokenError = '';
  token = '';
  passwordStrength: 'weak' | 'medium' | 'strong' = 'weak';
  passwordUpdated = false;

  showPassword = false;
  showConfirmPassword = false;

  turnstileToken: string | null = null;
  turnstileWidgetId: string | null = null;
  turnstileSiteKey: string = cloudflareConfig.turnstile.Site_Key;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.metaService.resetToDefault();

    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.tokenError = 'Token de recuperacion no encontrado.';
    }

    this.form = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(8), this.mediumPasswordValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    this.form.get('newPassword')?.valueChanges.subscribe(password => {
      this.updatePasswordStrength(password);
    });

  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.waitForTurnstile();
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser && this.turnstileWidgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (error) {
        console.error('Error al limpiar Turnstile:', error);
      }
    }
  }

  get passwordMismatch(): boolean {
    return !!this.form.errors?.['passwordMismatch'] && !!this.form.get('confirmPassword')?.touched;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onSubmit(): void {
    if (!this.token) {
      this.tokenError = 'Token de recuperacion no encontrado.';
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.turnstileToken) {
      this.errorMessage = 'Por favor complete la verificacion de seguridad';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authService.confirmPasswordReset({
      token: this.token,
      newPassword: this.form.value.newPassword,
      turnstileToken: this.turnstileToken
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = response.data?.message || response.message || 'Contrasena actualizada.';
          this.passwordUpdated = true;
          this.form.reset();
          this.showPassword = false;
          this.showConfirmPassword = false;
          this.resetTurnstile();
        } else {
          this.errorMessage = response.message || 'No se pudo actualizar la contrasena.';
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'No se pudo actualizar la contrasena.';
        this.isLoading = false;
        this.resetTurnstile();
      }
    });
  }

  private mediumPasswordValidator = (control: AbstractControl): ValidationErrors | null => {
    const password = String(control.value || '');
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const minLength = password.length >= 8;

    return hasUpperCase && hasLowerCase && hasNumber && minLength ? null : { mediumPassword: true };
  };

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  private updatePasswordStrength(password: string): void {
    const safePassword = String(password || '');
    if (!safePassword) {
      this.passwordStrength = 'weak';
      return;
    }

    const hasUpperCase = /[A-Z]/.test(safePassword);
    const hasLowerCase = /[a-z]/.test(safePassword);
    const hasNumber = /\d/.test(safePassword);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(safePassword);
    const minLength = safePassword.length >= 8;

    const checks = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, minLength].filter(Boolean).length;

    if (checks <= 2) {
      this.passwordStrength = 'weak';
    } else if (checks <= 4) {
      this.passwordStrength = 'medium';
    } else {
      this.passwordStrength = 'strong';
    }
  }

  private waitForTurnstile(): void {
    if (!this.isBrowser) return;

    if (window.turnstile) {
      this.renderTurnstile();
      return;
    }

    let attempts = 0;
    const maxAttempts = 50;
    const checkInterval = setInterval(() => {
      attempts++;
      if (window.turnstile) {
        clearInterval(checkInterval);
        this.renderTurnstile();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        this.errorMessage = 'Error al cargar el sistema de verificacion';
      }
    }, 100);
  }

  private renderTurnstile(): void {
    if (!this.isBrowser || !window.turnstile) return;

    const container = document.getElementById('turnstile-container-reset');
    if (!container) return;

    try {
      this.turnstileWidgetId = window.turnstile.render('#turnstile-container-reset', {
        sitekey: this.turnstileSiteKey,
        theme: 'light',
        callback: (token: string) => {
          this.turnstileToken = token;
          this.cdr.detectChanges();
        },
        'error-callback': () => {
          this.turnstileToken = null;
          this.cdr.detectChanges();
        },
        'expired-callback': () => {
          this.turnstileToken = null;
          this.cdr.detectChanges();
        }
      });
    } catch (error) {
      console.error('Error al renderizar Turnstile:', error);
    }
  }

  private resetTurnstile(): void {
    if (!this.isBrowser || !this.turnstileWidgetId || !window.turnstile) return;

    try {
      window.turnstile.reset(this.turnstileWidgetId);
      this.turnstileToken = null;
    } catch (error) {
      console.error('Error al resetear Turnstile:', error);
    }
  }
}
